import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { toast } from "react-toastify";
import { clientService } from "../../utils/services/clientService";
import { pipe17Service, needsOrgKey } from "../../utils/services/pipe17Service";

const steps = ["Select client", "Pipe17 credentials", "Backfill"];

const StatusRow = ({ done, label }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {done ? (
      <CheckCircleIcon color="success" fontSize="small" />
    ) : (
      <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
    )}
    <Typography variant="body2" color={done ? "text.primary" : "text.secondary"}>
      {label}
    </Typography>
  </Stack>
);

export default function Pipe17Onboard() {
  const [activeStep, setActiveStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const [orgKey, setOrgKey] = useState("");
  const [orgKeyNeeded, setOrgKeyNeeded] = useState(false);
  const [apiKey, setApiKey] = useState("");

  const [status, setStatus] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const fetchClients = async (search) => {
    setClientLoading(true);
    try {
      let query = "$limit=50";
      if (search) query += `&name[$search]=${encodeURIComponent(search)}`;
      const res = await clientService.list(query);
      setClients(res?.data || []);
    } catch (e) {
      console.error("Failed to load clients:", e);
    } finally {
      setClientLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const loadStatus = async (clientId) => {
    try {
      const res = await pipe17Service.status(clientId);
      setStatus(res);
      // Prefill org_key (not secret). The api_key is never returned by the API,
      // so on re-onboard the admin re-enters the original key.
      if (res?.pipe17_app_config) {
        setOrgKey(res.pipe17_app_config.org_key || "");
      }
    } catch (e) {
      console.error("Failed to load Pipe17 status:", e);
      setStatus(null);
    }
  };

  const onSelectClient = (client) => {
    setSelectedClient(client);
    setStatus(null);
    setOnboardResult(null);
    setBackfillResult(null);
    setOrgKey("");
    setOrgKeyNeeded(false);
    setApiKey("");
    if (client?._id) {
      loadStatus(client._id);
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  };

  const handleOnboard = async () => {
    if (!selectedClient?._id) return toast.error("Select a client first");
    if (!apiKey.trim()) return toast.error("API key is required");
    if (orgKeyNeeded && !orgKey.trim())
      return toast.error(
        "Pipe17 couldn't auto-detect this account's org key (no orders, products, or webhooks found yet) — enter the org key to continue",
      );
    setOnboarding(true);
    setOnboardResult(null);
    try {
      const payload = {
        client_id: selectedClient._id,
        api_key: apiKey.trim(),
      };
      // org_key is normally auto-resolved from the API key on the backend;
      // only send it once the backend has told us (via needsOrgKey) it
      // couldn't find any orders/products/webhooks to resolve it from.
      if (orgKeyNeeded && orgKey.trim()) payload.org_key = orgKey.trim();

      const res = await pipe17Service.onboard(payload);
      setOnboardResult(res);
      if (res?.success === false) {
        // Graceful failure (e.g. webhook registration failed): rows are saved,
        // so the admin can fix the key and retry without losing progress.
        if (needsOrgKey(res.message)) {
          setOrgKeyNeeded(true);
        }
        toast.error(res.message || "Onboarding did not complete — see details");
      } else {
        toast.success("Pipe17 client onboarded and webhook registered");
        setActiveStep(2);
      }
      await loadStatus(selectedClient._id);
    } catch (e) {
      const message = e.response?.data?.message || "Onboarding failed";
      // The backend couldn't read org_key off any orders/products/webhooks for
      // this key (typically a brand-new account) — surface the org key field
      // just for this retry so the operator can supply it manually.
      if (needsOrgKey(message)) {
        setOrgKeyNeeded(true);
      }
      toast.error(message);
      console.error(e);
    } finally {
      setOnboarding(false);
    }
  };

  const handleBackfill = async () => {
    if (!selectedClient?._id) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await pipe17Service.backfill(selectedClient._id);
      setBackfillResult(res);
      toast.success("Backfill triggered");
    } catch (e) {
      toast.error(e.response?.data?.message || "Backfill failed");
      console.error(e);
    } finally {
      setBackfilling(false);
    }
  };

  const hasAppConfig = !!status?.pipe17_app_config?.api_key_set;
  const hasWebhook = !!status?.pipe17_app_config?.webhook_secret_set;
  const hasOmsConfig = !!status?.oms_config;
  const webhookOk = !!onboardResult && onboardResult.success !== false;

  const clientLabel = (c) =>
    c ? `${c.business_name || c.name || "Unnamed"} — ${c.email || c._id}` : "";

  const resultBox = useMemo(() => {
    const payload = backfillResult || onboardResult;
    if (!payload) return null;
    return (
      <Box
        component="pre"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          bgcolor: "var(--bg-paper, #f5f5f5)",
          border: "1px solid rgba(0,0,0,0.12)",
          fontSize: 12,
          overflowX: "auto",
          maxHeight: 260,
        }}
      >
        {JSON.stringify(payload, null, 2)}
      </Box>
    );
  }, [onboardResult, backfillResult]);

  return (
    <Box sx={{ p: 3, maxWidth: 820, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Onboard a Pipe17 client
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pipe17 uses a single per-org API key (no OAuth) — enter it and the
        backend detects the org key and required permissions automatically,
        creates the credential + OMS config, and registers the Pipe17 webhook.
        You can then run a one-off backfill of the org&apos;s existing catalog
        and orders.
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        {/* Step 1 — client */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          1. Client
        </Typography>
        <Autocomplete
          options={clients}
          loading={clientLoading}
          value={selectedClient}
          getOptionLabel={clientLabel}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onChange={(_, v) => onSelectClient(v)}
          onInputChange={(_, v, reason) => {
            if (reason === "input") fetchClients(v);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select client"
              placeholder="Search by name"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {clientLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedClient && (
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            <StatusRow done={hasAppConfig} label="Original API key stored" />
            <StatusRow done={hasWebhook} label="Webhook registered (secret generated)" />
            <StatusRow done={hasOmsConfig} label="OMS config (provider: Pipe17)" />
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Step 2 — credentials */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          2. Pipe17 credentials
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Pipe17 API key"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            fullWidth
            required
            disabled={!selectedClient}
            helperText="The client's real Pipe17 API key, with access to webhooks, orders, products, and inventory. The org key and permissions are detected automatically — a separate webhook secret is also generated automatically."
          />
          {orgKeyNeeded && (
            <TextField
              label="Org key (orgKey)"
              value={orgKey}
              onChange={(e) => setOrgKey(e.target.value)}
              fullWidth
              required
              disabled={!selectedClient}
              helperText="Pipe17 couldn't auto-detect the org key for this account (no orders, products, or webhooks found yet) — enter it manually to continue."
            />
          )}
          <Box>
            <Button
              variant="contained"
              onClick={handleOnboard}
              disabled={!selectedClient || onboarding}
              startIcon={
                onboarding ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {hasAppConfig ? "Update & re-register webhook" : "Onboard client"}
            </Button>
          </Box>
          {onboardResult && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={webhookOk ? "success" : "warning"}
                label={
                  webhookOk
                    ? "Webhook registered"
                    : "Webhook registration failed"
                }
              />
            </Stack>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Step 3 — backfill */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          3. Backfill (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Webhooks only carry records changed after registration. Run a backfill
          to import the org&apos;s existing Pipe17 catalog and order history.
        </Typography>
        <Button
          variant="outlined"
          onClick={handleBackfill}
          disabled={!hasOmsConfig || backfilling}
          startIcon={
            backfilling ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Run backfill
        </Button>

        {resultBox}
      </Paper>
    </Box>
  );
}
